import axios from 'axios';
import camelcase from 'camelcase';
import {
  concat,
  forEach,
  keys,
  map,
  reduce,
  sum,
  toLower,
  values,
} from 'lodash-es';
import { API_BASE } from '@/env';
import { createTracer } from '@/otel';

const traceMethod = createTracer('Api');

console.warn('Legacy API imported');

export class Api {
  constructor(t) {
    if (!t) {
      console.warn('API initialized without the ability to translate.');
      this.$t = () =>
        console.error(
          'API tried to translate without the translation function.',
        );
    } else {
      this.$t = t;
    }
  }

  @traceMethod
  async getPromoInfo(code) {
    return axios(`${API_BASE}/promo/${code}/`).then((resp) => resp.data.amount);
  }

  @traceMethod
  async getYachtCountries() {
    return axios(`${API_BASE}/yacht-countries`).then(({ data }) => {
      const unifiedData = map(data, (item) => {
        const count = item.yacht_count;

        delete item.yacht_count;

        return {
          ...item,
          count,
        };
      });

      return [
        {
          code: this.$t('LABELS.anywhere'),
          added: true,
          name: {
            cs: this.$t('LABELS.anywhere'),
            sk: this.$t('LABELS.anywhere'),
          },
          count: sum(map(unifiedData, (item) => item.count)),
        },
      ].concat(unifiedData);
    });
  }

  @traceMethod
  async getShipCruises(shipCode, itineraryCode) {
    return axios(
      `${API_BASE}/cruises?perPage=10&ships[]=${shipCode}&pagination=false&itinerary=${itineraryCode}&orderBy=soon`,
    ).then((resp) => {
      const data = resp.data.data;

      return this.unifyCruises(data, true);
    });
  }

  @traceMethod
  async getFavoriteCruises() {
    return axios(
      `${API_BASE}/cruises?perPage=3&orderBy=favorite&limit=3&pagination=false`,
    ).then((resp) => {
      const data = resp.data.data;

      return this.unifyCruises(data, true);
    });
  }

  @traceMethod
  async getPromoCruises() {
    return axios(
      `${API_BASE}/cruises?perPage=3&onlyPromo=true&orderBy=favorite&pagination=false`,
    ).then((resp) => {
      const data = resp.data.data;

      return this.unifyCruises(data, true);
    });
  }

  /**
   *
   * @param {string} cruise_code
   * @param {bool} simplified If true, will not load port or itinerary data
   * @returns Detailed cruise information
   */
  @traceMethod
  async getCruiseDetail(cruise_code, simplified = false) {
    return axios(`${API_BASE}/cruise/${cruise_code}`).then((resp) => {
      const data = resp.data;

      if (!data || data.length === 0) {
        return false;
      }

      // Unification
      return this.unifyCruises([data], !simplified, !simplified).then(
        (unifiedData) => {
          console.log(unifiedData[0]);

          return unifiedData[0];
        },
      );
    });
  }

  @traceMethod
  unifyCategoryListApi(categoryList) {
    if (Array.isArray(categoryList)) {
      // Group categories by their 'category' field
      const grouped = categoryList.reduce((acc, c) => {
        if (!acc[c.category]) {
          acc[c.category] = [];
        }
        acc[c.category].push(c);
        return acc;
      }, {});

      return Object.keys(grouped).map((categoryKey) => {
        const entries = grouped[categoryKey];
        const firstEntry = entries[0];

        const prices = {};
        const applicableItemsByPriceType = {};
        const priceCodesByPriceType = {};

        // Process each entry of the same category
        entries.forEach((c) => {
          const applicableItems =
            c.priceCategoryItems.componentDetails[0].itemDetails
              .filter((item) => item.itemApplicability)
              .map(
                (item) =>
                  `${item.packageCode}:${item.itemCode}:${item.itemTypeCode}`,
              );

          prices[c.priceType] = c.totalPrice;
          applicableItemsByPriceType[c.priceType] = applicableItems;
          priceCodesByPriceType[c.priceType] = c.priceCode;
        });

        // Use only the category itself as the 'code', instead of combining it with `priceCode`.
        // Each priceType can have its own distinct priceCode stored in priceCodesByPriceType.
        return {
          code: firstEntry.category,
          category: firstEntry.category,
          fare: firstEntry.priceDesc,
          name: {
            cs: firstEntry.priceDesc,
            sk: firstEntry.priceDesc,
          },
          cabinLocation: {
            cs: 'Inside', // or any fallback
            sk: 'Inside',
          },
          availability: firstEntry.cabinsAvailable > 0,
          price: firstEntry.totalPrice,
          price_port: firstEntry.portChargesAmt,
          prices: prices,
          applicableItemsByPriceType: applicableItemsByPriceType,
          priceCodesByPriceType: priceCodesByPriceType,
        };
      });
    }

    return reduce(
      values(categoryList),
      (acc, subCategories) => {
        const k = keys(subCategories);

        const prices = {};
        let subCategory;

        forEach(k, (key) => {
          prices[key] = subCategories[key].price;
          subCategory = subCategories[key];
        });

        return concat(acc, {
          ...subCategory,
          prices,
        });
      },
      [],
    );
  }

  @traceMethod
  async addShipGallery(ship) {
    if (!ship || !ship.id) {
      return ship; // Return the ship unchanged if it's null or has no ID
    }

    return this.getEntityFiles(ship.id).then((shipFiles) => {
      const gallery = shipFiles
        .filter((file) => file.function === 'gallery-img')
        .map((img) => {
          img.url = `${API_BASE}/file/${img.id}`;
          return img;
        });

      ship.gallery = gallery;
      return ship;
    });
  }

  @traceMethod
  async getOffers(workflowId, cabinCode, cabinPrice, packageCode) {
    const url = cabinCode
      ? `/workflow/other?workflowId=${workflowId}&categoryCode=${cabinCode}&fakeTotal=${cabinPrice}&fareCode=${packageCode}`
      : `/workflow/other?workflowId=${workflowId}`;

    return axios(`${API_BASE}${url}`).then((resp) =>
      this.unifyCruises([resp.data.cruise], true).then((unifiedCruises) => {
        const offers = [];

        const unifiedCruise = unifiedCruises[0];
        unifiedCruise.summaryList = [];
        if (resp.data.workflow.guest_count_adult > 0) {
          unifiedCruise.summaryList.push([
            this.$t('LABELS.adults-with-age'),
            resp.data.workflow.guest_count_adult,
          ]);
        }
        if (resp.data.workflow.guest_count_child > 0) {
          unifiedCruise.summaryList.push([
            this.$t('LABELS.children-with-age'),
            resp.data.workflow.guest_count_child,
          ]);
        }
        unifiedCruise.fullPrice = resp.data.workflow.price_total;

        return {
          workflow: resp.data.workflow,
          offers: offers,
          cruise: unifiedCruise,
        };
      }),
    );
  }

  // Helper to strip off any appended items after the second semicolon
  normalizeCode(fullCode) {
    if (!fullCode) return fullCode;
    const parts = fullCode.split(';');
    // If code is "SXC00399AR4019PR;IM1" (no items), parts.length = 2
    // If code is "SXC00399AR4019PR;IM1;726964690:EXP2B:OBS|..." (with items), parts.length >= 3
    // We only keep the first 2 segments: [priceCode, category]
    return parts.length >= 2 ? parts[1] : fullCode;
  }

  @traceMethod
  async getPassengers(workflowId) {
    return axios(`${API_BASE}/workflow/fill-up?workflowId=${workflowId}`).then(
      (resp) => {
        if (resp.data.error) {
          return { workflow: null, passengers: null, cruise: null };
        }

        return this.unifyCruises([resp.data.cruise], true).then(
          (unifiedCruises) => {
            const unifiedCruise = unifiedCruises[0];
            unifiedCruise.summaryList = [];
            if (resp.data.workflow.guest_count_adult > 0) {
              unifiedCruise.summaryList.push([
                this.$t('LABELS.adults-with-age'),
                resp.data.workflow.guest_count_adult,
              ]);
            }
            if (resp.data.workflow.guest_count_child > 0) {
              unifiedCruise.summaryList.push([
                this.$t('LABELS.children-with-age'),
                resp.data.workflow.guest_count_child,
              ]);
            }

            const categoryCode = resp.data.workflow.category_code;
            const fareCode = resp.data.workflow.fare_code;
            const categories = this.unifyCategoryListApi(
              resp.data.category_list_api.Cruises ||
                resp.data.category_list_api,
            );

            unifiedCruise.fullPrice = categories.find(
              (cabin) => cabin.code === this.normalizeCode(categoryCode),
            )?.prices?.[fareCode];

            resp.data.passenger_list.map((pass) => {
              pass.prefix = pass.phone
                ? pass.phone.slice(0, pass.phone.length - 9)
                : '';
              pass.phone = pass.phone
                ? pass.phone.slice(pass.prefix.length)
                : '';
            });

            resp.data.workflow.components = resp.data.components;

            return {
              workflow: resp.data.workflow,
              passengers: resp.data.passenger_list,
              cruise: unifiedCruise,
            };
          },
        );
      },
    );
  }

  @traceMethod
  async setPassengers(workflowId, passengers, userId) {
    return fetch(
      `${API_BASE}/workflow/fill-up?workflowId=${workflowId}&clientId=${userId}`,
      {
        body: JSON.stringify({ passengerList: passengers }),
        method: 'POST',
      },
    ).then(() => {});
  }

  @traceMethod
  async createBooking(workflowId, body) {
    return fetch(
      `${API_BASE}/workflow/create-booking?workflowId=${workflowId}`,
      {
        body: JSON.stringify(body),
        method: 'POST',
      },
    )
      .then((resp) => resp.json())
      .then((json) => ({ data: json }));
  }

  @traceMethod
  async updateBooking(workflowId, body) {
    return fetch(`${API_BASE}/workflow/other?workflowId=${workflowId}`, {
      body: JSON.stringify(body),
      method: 'POST',
    })
      .then((resp) => resp.json())
      .then((json) => ({ data: json }));
  }

  @traceMethod
  async holdCabin(workflowId) {
    return axios.post(
      `${API_BASE}/workflow/hold-cabin?workflowId=${workflowId}`,
    );
  }

  @traceMethod
  async getBookingInfo(workflowId) {
    return axios(
      `${API_BASE}/workflow/show-booking?workflowId=${workflowId}`,
    ).then((resp) => {
      if (resp.data.error) {
        return null;
      }

      return resp.data.booking;
    });
  }

  @traceMethod
  async registerAccount(email, password) {
    return axios
      .post(`${API_BASE}/register`, {
        password,
        email,
      })
      .then(() => true)
      .catch(() => false);
  }

  @traceMethod
  async recoverPassword(email) {
    return axios
      .post(`${API_BASE}/lost-password/${email}`)
      .then((resp) => resp.status === 204);
  }

  @traceMethod
  async getDials() {
    return axios.post(`${API_BASE}/`).then((resp) => resp.data?.dials);
  }

  @traceMethod
  async getPort(portCode) {
    return await axios(`${API_BASE}/port/${portCode}`).then(
      (resp) => resp.data,
    );
  }

  @traceMethod
  async getRecommendedYachts(yachtId, date) {
    const filter = { departureDayFrom: date, perPage: 4, orderBy: 'soon' };
    return this.searchYachts(filter).then((yachts) =>
      yachts.data.filter((yacht) => yacht.id !== yachtId).slice(0, 3),
    );
  }

  @traceMethod
  async createReservation(clientId, data) {
    return fetch(`${API_BASE}/reservation?clientId=${clientId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
      .then((resp) => resp.json())
      .then((json) => ({ data: json }));
  }

  // Utilities
  /**
   *
   * @param {array} cruisesData RAW data from the API to unify
   * @param {bool} loadPortData Load port information for first port
   * @param {bool} loadItineraryPortsData Load port information for all ports
   * @returns
   */
  @traceMethod
  async unifyCruises(
    cruisesData,
    loadPortData = false,
    loadItineraryPortsData = false,
  ) {
    const allUnifiedData = [];

    // Collect Port Codes and Prepare Promises:
    const portPromises = [];
    const uniquePortCodes = new Set(); // Use a Set to avoid duplicate port fetches

    for (const data of cruisesData) {
      if (data.itinerary_steps_simplified === undefined) {
        continue;
      }
      const unifiedData = {};
      unifiedData.id = data.id;
      unifiedData.days = data.days;
      unifiedData.departure_port_name = data.departure_port_name;
      unifiedData.arrival_port_name = data.arrival_port_name;
      unifiedData.destination_name = data.destination_name;
      unifiedData.full_itinerary = data.itinerary_steps;
      unifiedData.itinerary = data.itinerary_steps_simplified;
      unifiedData.itinerary_code = data.itinerary_code;
      unifiedData.departure_date = data.departure_date;
      unifiedData.arrival_date = data.arrival_date;
      unifiedData.price = data.price;
      unifiedData.cruise_code = data.code;
      unifiedData.ship_name = data.ship_name;
      unifiedData.ship_code = data.ship_code;
      unifiedData.ship_id = data.ship_id;
      unifiedData.features = data.features;
      unifiedData.imported_at = data.imported_at;
      unifiedData.import_source = data.import_source;
      unifiedData.flight_mandatory = data.flight_mandatory;
      unifiedData.map_url = data.map_url;
      unifiedData.ship_stars = data.ship_stars;
      unifiedData.promo = data.promo;
      unifiedData.promo_fare = data.promo_fare;
      unifiedData.people = data.people;

      if (
        (loadPortData || loadItineraryPortsData) &&
        unifiedData.full_itinerary &&
        unifiedData.full_itinerary.length > 0
      ) {
        if (loadItineraryPortsData) {
          // Load data for all ports
          for (const step of unifiedData.full_itinerary) {
            for (const portType of ['arrival', 'departure']) {
              const portCode = step[`${portType}_port_code`];
              if (portCode && !uniquePortCodes.has(portCode)) {
                uniquePortCodes.add(portCode);
                portPromises.push(this.getPort(portCode));
              }
            }
          }
        } else {
          // Load data only for the first port
          const portCode = unifiedData.full_itinerary[0].departure_port_code;
          // Add to Set only if not already present
          if (portCode && !uniquePortCodes.has(portCode)) {
            uniquePortCodes.add(portCode);
            portPromises.push(this.getPort(portCode)); // Push the Promise to the array
          }
        }
      }

      // unifiedData.prices = {
      //   single: data.price_person,
      //   cabin: data.price,
      // };

      // unifiedData.priceDouble = data.price_double;

      Object.keys(unifiedData.itinerary).forEach((lang) => {
        const itinerary = unifiedData.itinerary[lang];

        if (itinerary[4] === null) {
          itinerary[4] = '...';
        }
      });

      // Placeholder
      unifiedData.transport_included = true;

      allUnifiedData.push(unifiedData);
    }

    if (!loadPortData && !loadItineraryPortsData) {
      return allUnifiedData;
    }

    let fetchingPromise;
    if (loadPortData) {
      // If true, fetch the ports.
      fetchingPromise = Promise.all(portPromises);
    } else {
      // If false, mimic the original by resolving immediately with an empty array.
      fetchingPromise = Promise.resolve([]);
    }

    return fetchingPromise.then((ports) => {
      // Assign Port Images to Cruises:
      for (const unifiedData of allUnifiedData) {
        if (
          unifiedData.full_itinerary &&
          unifiedData.full_itinerary.length > 0
        ) {
          const portCode = unifiedData.full_itinerary[0].departure_port_code;

          // Find the corresponding port from the fetched ports
          const portInfo = ports.find((port) => port.code === portCode); // Assumes port.code exists
          if (portInfo) {
            unifiedData.image = portInfo.img_url;
          }

          // Add descriptions to all ports in full_itinerary
          for (const step of unifiedData.full_itinerary) {
            for (const portType of ['arrival', 'departure']) {
              const portCode = step[`${portType}_port_code`];
              if (portCode) {
                const portInfo = ports.find((port) => port.code === portCode);
                if (portInfo) {
                  step[`${portType}_port_description`] = portInfo.description;
                }
              }
            }
          }
        }
      }

      return allUnifiedData;
    });
  }

  @traceMethod
  async updateWorkflowId(oldWorkflowId) {
    const workflows = localStorage.getItem('workflows')
      ? JSON.parse(localStorage.getItem('workflows'))
      : {};
    const cruiseCode =
      Object.keys(workflows)[Object.values(workflows).indexOf(oldWorkflowId)];

    return this.initWorkflow(cruiseCode).then((newWorkflowId) => {
      workflows[cruiseCode] = newWorkflowId;
      localStorage.setItem('workflows', JSON.stringify(workflows));

      return newWorkflowId;
    });
  }

  getLastMonthDayDate(date) {
    const workingDate = new Date(date);
    workingDate.setDate(1);
    workingDate.setMonth(workingDate.getMonth() + 1);
    workingDate.setTime(workingDate.getTime() - 86400000);

    return `${workingDate.getFullYear()}-${
      workingDate.getMonth() + 1
    }-${workingDate.getDate()}`;
  }

  @traceMethod
  async unifyYachts(yachtsData) {
    return this.getYachtCountries().then((countries) => {
      const allUnifiedData = Promise.all(
        yachtsData.map((data) => {
          const unifiedData = {};
          unifiedData.id = data.id;
          unifiedData.code = data.code;
          unifiedData.duration = data.days;
          unifiedData.transportIncluded = data.transit;
          unifiedData.destinationName = data.destination_name;
          unifiedData.destinationCode = data.destination_code;
          unifiedData.name = data.name;
          unifiedData.yachtName = data.name;
          unifiedData.transports = {
            plane: data?.plane,
            car: data?.car,
          };
          unifiedData.description = data.description;
          unifiedData.theme = data.theme_code;
          unifiedData.themeName = data.theme_name;
          unifiedData.type = data.type_code;
          unifiedData.typeName = data.type_name;
          unifiedData.departureName = data.departure_name;
          unifiedData.departureDate = data.departure_at;
          unifiedData.arrivalDate = data.arrival_at;
          unifiedData.arrivalName = data.arrival_name;
          unifiedData.sea = data.sea;
          unifiedData.sliderName = data.slider_name;
          unifiedData.sliderDescription = data.slider_description;
          unifiedData.country = countries.find(
            (country) => country.code === data.country_code,
          );
          unifiedData.label = data.label;
          unifiedData.freeUnits = {
            single: data.free_person,
            cabin: data.free_cabin,
            yacht: data.free_ship,
          };

          unifiedData.prices = {
            single: data.price_person,
            cabin: data.price_cabin,
            yacht: data.price_boat,
          };
          unifiedData.pricesFull = {
            single: data.price_person_full,
            cabin: data.price_cabin_full,
            yacht: data.price_boat_full,
          };

          return this.getEntityFiles(data.id).then((entityFiles) => {
            const thumbnailImg = entityFiles.find(
              (file) => file.function === 'thumbnail-img',
            );
            const heroImg = entityFiles.find(
              (file) => file.function === 'hero-img',
            );
            const galleryImgs = entityFiles.filter(
              (file) => file.function === 'gallery-img',
            );
            const slidesImgs = entityFiles.filter(
              (file) => file.function === 'slides-img',
            );
            const slidesFile = entityFiles.find(
              (file) => file.function === 'slides-file',
            );

            if (thumbnailImg) {
              unifiedData.image = `${API_BASE}/file/${thumbnailImg.id}`;
            } else {
              unifiedData.image = './';
            }

            if (heroImg) {
              unifiedData.heroImage = `${API_BASE}/file/${heroImg.id}`;
            } else {
              unifiedData.heroImage = './';
            }

            unifiedData.gallery = galleryImgs.map((img) => {
              img.url = `${API_BASE}/file/${img.id}`;
              return img;
            });

            unifiedData.slides = slidesImgs.map((img) => {
              img.url = `${API_BASE}/file/${img.id}`;
              return img;
            });

            if (slidesFile) {
              unifiedData.slidesFile = `${API_BASE}/file/${slidesFile.id}`;
            }

            return unifiedData;
          });
        }),
      );

      return allUnifiedData;
    });
  }

  @traceMethod
  async getEntityFiles(entityId) {
    return axios(`${API_BASE}/files?entityId=${entityId}`).then(
      (resp) => resp.data,
    );
  }

  @traceMethod
  async getFavoriteYachts() {
    return axios(`${API_BASE}/yachts?perPage=3&orderBy=favorite`)
      .then((resp) => resp.data)
      .then((data) => this.unifyYachts(data, true));
  }

  @traceMethod
  async searchYachts(filters, perPage = 5) {
    const allowedFilters = [
      'countryCode',
      'departureDayFrom',
      'departureDayTo',
      'adultCount',
      /*'transportIncluded',*/ 'page',
      'orderBy',
      'yachtDaysMax',
      'priceMin',
      'priceMax',
      'priceCurrency',
      'themeCode',
      'typeCode',
      'perPage',
    ];

    const searchFilters = [];

    allowedFilters.forEach((filterName) => {
      const filterValue = filters[filterName];

      if (filterValue) {
        searchFilters.push(`${filterName}=${filterValue}`);
      }
    });

    const searchUrl = `?perPage=${perPage}&${searchFilters.join('&')}`;
    return axios(`${API_BASE}/yachts${searchUrl}`).then((resp) => {
      return this.unifyYachts(resp.data.data, true).then((data) => {
        const meta = {
          currentPage: resp.data.page,
          maxPage: resp.data.page_count,
        };

        return { meta, data };
      });
    });
  }

  @traceMethod
  async getYachtDetail(yachtCode) {
    return axios(`${API_BASE}/yacht/${yachtCode}`).then((resp) => {
      const data = resp.data;

      if (!data || data.length === 0) {
        return false;
      }

      // Unification
      return this.unifyYachts([data], true).then(
        (unifiedData) => unifiedData[0],
      );
    });
  }

  @traceMethod
  async loadOrders(email, token) {
    return axios
      .post(`${API_BASE}/user/orders`, {
        email,
        token,
      })
      .then((resp) => {
        const states = {
          completed: this.$t('STATUSES.completed'),
          cancelled: this.$t('STATUSES.cancelled'),
          cancelledPaidDeposit: this.$t('STATUSES.cancelled'),
          cancelledPaidFull: this.$t('STATUSES.cancelled'),
          paidDeposit: this.$t('STATUSES.paid-deposit'),
          paidFull: this.$t('STATUSES.paid-full'),
          waitingForPayment: this.$t('STATUSES.waiting-for-payment'),
        };

        const promises = resp.data.map((dataItem) => {
          const newDataItem = {};
          Object.keys(dataItem).forEach((key) => {
            newDataItem[camelcase(key)] = dataItem[key];
          });
          newDataItem.type = newDataItem.workflow?.id
            ? 'cruiser'
            : newDataItem.reservation?.created_at
              ? 'yacht'
              : null;
          newDataItem.stateName =
            states[newDataItem.state] ?? newDataItem.state;

          return this.getEntityFiles(newDataItem.id).then((files) => {
            newDataItem.files = files.filter(
              (file) => file.function === 'order-file',
            );

            if (newDataItem.type === 'yacht') {
              let reservationType;
              if (newDataItem.reservation.count_cabin > 0) {
                reservationType = this.$t('LABELS.cabin');
              } else if (newDataItem.reservation.count_person > 0) {
                reservationType = this.$t('LABELS.person');
              } else if (newDataItem.reservation.count_ship > 0) {
                reservationType = this.$t('LABELS.ship');
              }
              newDataItem.reservationType = reservationType;
              newDataItem.departureFrom =
                newDataItem.reservation.yacht.destination_name;
              newDataItem.price = newDataItem.total;
              newDataItem.arrivalAt = newDataItem.reservation.yacht.arrival_at;
              newDataItem.amount = Math.max(
                newDataItem.reservation.count_cabin,
                newDataItem.reservation.count_person,
                newDataItem.reservation.count_ship,
              );

              newDataItem.passengers = [
                {
                  birth_date:
                    newDataItem.reservation.responsible_passenger_birth_date,
                  email: newDataItem.reservation.responsible_passenger_email,
                  first_name:
                    newDataItem.reservation.responsible_passenger_first_name,
                  last_name:
                    newDataItem.reservation.responsible_passenger_last_name,
                  nationality_code:
                    newDataItem.reservation
                      .responsible_passenger_nationality_code,
                  phone: newDataItem.reservation.responsible_passenger_phone,
                  sex: newDataItem.reservation.responsible_passenger_sex,
                  type: 'Adult',
                },
              ];
            } else if (newDataItem.type === 'cruiser') {
              newDataItem.price = newDataItem.total;
              newDataItem.adults = newDataItem.workflow.guest_count_adult;
              newDataItem.children = newDataItem.workflow.guest_count_child;
              newDataItem.departureFrom =
                newDataItem.workflow.cruise?.departure_port_name;
              newDataItem.arrivalAt = newDataItem.workflow.cruise?.arrival_date;
              newDataItem.passengers = newDataItem.workflow.guest_list;
            }

            return newDataItem;
          });
        });

        return Promise.all(promises).then((data) =>
          data.filter((x) => x.price),
        );
      });
  }

  @traceMethod
  async updateUser(email, token, userData) {
    return axios
      .post(`${API_BASE}/update-user`, { ...userData, authemail: email, token })
      .then((resp) => resp.data)
      .catch(() => false);
  }

  @traceMethod
  async unifyContent(data, typeCode) {
    return this.getEntityFiles(data.id).then((contentFiles) => {
      const unifiedData = {};

      unifiedData.heroImage = contentFiles
        .filter((file) => file.function === 'hero-img')
        .map((file) => {
          file.url = `${API_BASE}/file/${file.id}`;
          return file;
        })[0];
      unifiedData.gallery = contentFiles
        .filter((file) => file.function === 'gallery-img')
        .map((file) => {
          file.type = 'image';
          file.src = `${API_BASE}/file/${file.id}`;
          file.thumb = `${API_BASE}/file/${file.id}`;
          return file;
        });

      unifiedData.title = data.title;
      unifiedData.description = data.description;
      unifiedData.heading = data.headline;
      unifiedData.subheading = data.lead;
      unifiedData.content = data.content;

      unifiedData.listHeading = data.list_name;

      unifiedData.moreUrl = data.link_url;
      unifiedData.moreLabel = data.link_label;

      unifiedData.metaTitle = data.title;
      unifiedData.metaDescription = data.description;

      unifiedData.yachts = [];
      unifiedData.cruises = [];

      if (typeCode.startsWith('y')) {
        return Promise.all(
          data.yacht_code_list.map((yachtCode) =>
            this.getYachtDetail(yachtCode),
          ),
        )
          .then((yachts) => yachts.filter((yacht) => yacht?.code))
          .then((yachts) => {
            unifiedData.yachts = yachts;
            return unifiedData;
          });
      } else if (typeCode.startsWith('c')) {
        return Promise.all(
          data.cruise_code_list.map((cruiseCode) =>
            this.getCruiseDetail(cruiseCode),
          ),
        )
          .then((cruises) => cruises.filter((cruise) => cruise?.cruise_id))
          .then((cruises) => {
            unifiedData.cruises = cruises;
            return unifiedData;
          });
      }

      return unifiedData;
    });
  }

  @traceMethod
  async loadContent(name, typeCode) {
    return axios(`${API_BASE}/content-page/${typeCode}/${name}`).then(
      (resp) => {
        if (resp.data.constructor === Array) {
          return null;
        }

        return this.unifyContent(resp.data, typeCode);
      },
    );
  }

  @traceMethod
  async getBanner(type, language, position, list = false) {
    const siteShortcut = type.slice(0, 1) + language.slice(0, 1);

    const request = list
      ? axios(`${API_BASE}/banners/${siteShortcut}/${position}/`)
      : axios(`${API_BASE}/banner/${siteShortcut}/${position}/`).then(
          (resp) => {
            resp.data = [resp.data];
            return resp;
          },
        );

    return request
      .then((resp) => {
        const unifiedBanners = resp.data.map((data) => {
          return {
            url: data.url,
            desktop: {
              source: `${API_BASE}/file/${data.fileDesktop}`,
              type: data.typeDesktop,
            },
            mobile: {
              source: `${API_BASE}/file/${data.fileMobile}`,
              type: data.typeMobile,
            },
          };
        });

        return list ? unifiedBanners : unifiedBanners[0];
      })
      .catch((e) => {
        console.error(e);
        return null;
      });
  }

  @traceMethod
  async getShipDetail(shipCode) {
    return axios(`${API_BASE}/ships`).then((resp) => {
      const ship = resp.data.find((ship) => ship.code === shipCode);

      if (!ship) {
        return false;
      }

      return this.getEntityFiles(ship.id).then((shipFiles) => {
        const galleryImages = shipFiles.filter(
          (file) => file.function === 'gallery-img',
        );
        ship.galleryImages = galleryImages.map(
          (file) => `${API_BASE}/file/${file.id}`,
        );

        return axios(`${API_BASE}/cabins/${ship.id}`).then((resp) => {
          return Promise.all(
            resp.data.map((cabin) => {
              // Cabins
              if (!cabin.id) {
                cabin.id = `0434998e-d5bf-47a5-8927-78d2bd9e700${i}`;
              }

              return this.getEntityFiles(cabin.id).then((cabinFiles) => {
                const cabinImages = cabinFiles.filter(
                  (file) => file.function === 'gallery-img',
                );
                cabin.cabinImages = cabinImages.map(
                  (file) => `${API_BASE}/file/${file.id}`,
                );

                return cabin;
              });
            }),
          ).then((cabins) => {
            ship.cabins = cabins;
            ship.heroVideo = this.createFilePath(ship.hero_video);

            delete ship.hero_video;

            return ship;
          });
        });
      });
    });
  }

  @traceMethod
  async getShipImgUrl(shipId) {
    return this.getEntityFiles(shipId).then((shipFiles) => {
      const galleryImages = shipFiles.filter(
        (file) => file.function === 'gallery-img',
      );

      // Return first image url or null if no exist
      return galleryImages.length > 0
        ? `${API_BASE}/file/${galleryImages[0].id}`
        : null;
    });
  }

  @traceMethod
  async tryRegisterAccount(email, accountData = {}) {
    return axios
      .post(`${API_BASE}/register`, {
        ...accountData,
        email,
      })
      .then((resp) => resp.data.id)
      .catch(() => false);
  }

  @traceMethod
  async getFlights(workflowId) {
    return axios(
      `${API_BASE}/workflow/categories?workflowId=${workflowId}`,
    ).then((resp) => {
      if (resp.data.error) {
        return { flights: [], workflow: null, cruise: null };
      }

      const localeFlightName = (flight) => {
        switch (toLower(flight)) {
          case 'prague': {
            return 'Praha';
          }
          case 'vienna': {
            return 'Vídeň';
          }
          case 'munich': {
            return 'Mnichov';
          }
          default: {
            return flight;
          }
        }
      };

      const flights = Object.keys(resp.data.flights).map((key) => {
        return {
          code: key,
          text: localeFlightName(resp.data.flights[key]),
        };
      });

      resp.data.workflow.components = resp.data.components;

      return this.unifyCruises([resp.data.cruise], true).then(
        (unifiedCruises) => {
          const unifiedCruise = unifiedCruises[0];
          unifiedCruise.summaryList = [];
          if (resp.data.workflow.guest_count_adult > 0) {
            unifiedCruise.summaryList.push([
              this.$t('LABELS.adults-with-age'),
              resp.data.workflow.guest_count_adult,
            ]);
          }
          if (resp.data.workflow.guest_count_child > 0) {
            unifiedCruise.summaryList.push([
              this.$t('LABELS.children-with-age'),
              resp.data.workflow.guest_count_child,
            ]);
          }

          return {
            workflow: resp.data.workflow,
            flights: flights,
            cruise: unifiedCruise,
          };
        },
      );
    });
  }

  @traceMethod
  async setFlight(workflowId, flightCode) {
    if (flightCode === null) {
      flightCode = '';
    }
    return axios(
      `${API_BASE}/workflow/set-flight?workflowId=${workflowId}&flight=${flightCode}`,
    ).then(() => {});
  }

  @traceMethod
  async getWorkflow(workflowId) {
    return axios(`${API_BASE}/get-workflow/${workflowId}`).then(
      (resp) => resp.data,
    );
  }

  @traceMethod
  async getSnippet(name) {
    return axios(`${API_BASE}/snippet/${name}/`).then((resp) => resp.data);
  }

  @traceMethod
  async getYachtasHowItWorks() {
    return axios(`${API_BASE}/how-it-works/ys`).then(({ data }) => {
      return map(data, (item) => {
        const backgroundUrl = this.createFilePath(item.background_file);

        delete item.background_file;

        return {
          ...item,
          backgroundUrl,
        };
      }).sort((x, y) => x.order - y.order);
    });
  }

  createFilePath(id, thumbnail = false) {
    if (!id) return '';

    const filePath = thumbnail ? 'file-thumbnail' : 'file';
    return `${API_BASE}/${filePath}/${id}`;
  }

  @traceMethod
  async getYachtasMainPageGallery() {
    return axios(`${API_BASE}/files/?function=homepage-carrousel`).then(
      ({ data }) => {
        return map(data, (item) => ({
          type: item.type,
          name: item.name,
          order: item.order,
          src: this.createFilePath(item.id),
          thumb: this.createFilePath(item.id, true),
        })).sort((x, y) => x.order - y.order);
      },
    );
  }

  @traceMethod
  async getAboutYachtingDashesInfo() {
    return axios(`${API_BASE}/files/?function=homepage-yachting`).then(
      ({ data }) => {
        return map(data, (item) => ({
          type: item.type,
          name: item.name,
          order: item.order,
          src: this.createFilePath(item.id),
          thumb: this.createFilePath(item.id, true),
        })).sort((x, y) => x.order - y.order);
      },
    );
  }

  @traceMethod
  async getMenu(type, lang, position = 'header') {
    return axios(`${API_BASE}/menu/${type[0]}${lang[0]}`).then(({ data }) =>
      map(data?.[position], (item) => ({
        ...item,
        url: `/kontent/${item.url}`,
      })),
    );
  }

  @traceMethod
  async searchCruisesDelegate(filters, options = {}) {
    const { perPage = 10, signal } = options;

    const allowedFilters = [
      'destinations',
      'departureDayFrom',
      'departureDayTo',
      'adultCount',
      'childCount',
      'page',
      'orderBy',
      'daysMax',
      'priceMin',
      'priceMax',
      'priceCurrency',
      'ships',
      'stars',
      'cruiseDaysMin',
      'cruiseDaysMax',
    ];

    // Use URLSearchParams to build a safe query string.
    const params = new URLSearchParams();
    params.append('perPage', perPage);
    params.append('withDelegate', true);

    allowedFilters.forEach((filterName) => {
      const filterValue = filters[filterName];

      if (
        filterValue !== undefined &&
        filterValue !== null &&
        filterValue !== ''
      ) {
        if (Array.isArray(filterValue)) {
          // For any array input, append each element with filterName[]
          filterValue.forEach((value) => {
            params.append(`${filterName}[]`, value);
          });
        } else {
          params.append(filterName, filterValue);
        }
      }
    });

    const searchUrl = params.toString();

    return axios(`${API_BASE}/special-cruises?${searchUrl}`, { signal }).then(
      (resp) =>
        this.unifyCruisesDelegate(resp.data.data).then((data) => {
          const meta = {
            currentPage: resp.data.page,
            maxPage: resp.data.page_count,
          };

          return { meta, data };
        }),
    );
  }

  @traceMethod
  async getCruisesDelegateDetail(cruiseCode) {
    return axios(`${API_BASE}/special-cruise/${cruiseCode}`).then((resp) => {
      const data = resp.data;

      if (!data || data.length === 0) {
        return false;
      }

      // Unification
      return this.unifyCruisesDelegate([data], true).then(
        (unifiedData) => unifiedData[0],
      );
    });
  }

  @traceMethod
  async getSpecialCruisePrices(cruiseUuid) {
    return axios(`${API_BASE}/special-cruise-price/${cruiseUuid}`).then(
      (resp) => {
        const data = resp.data;

        if (!data || data.length === 0) {
          return false;
        }

        return data;
      },
    );
  }

  @traceMethod
  async getSpecialCruiseItinerary(cruiseUuid) {
    return axios(`${API_BASE}/special-cruise-itinerary/${cruiseUuid}`).then(
      (resp) => {
        const data = resp.data;

        if (!data) {
          return false;
        }

        return data;
      },
    );
  }

  @traceMethod
  async sendCruisesDelegateOrderForm(body) {
    return fetch(`${API_BASE}/cruise-delegate-order-email/`, {
      body: JSON.stringify(body),
      method: 'POST',
    })
      .then((resp) => resp.json())
      .then((json) => ({ data: json }));
  }

  @traceMethod
  async unifyCruisesDelegate(cruisesData) {
    return Promise.all(
      cruisesData.map((data) => {
        const unifiedData = {};
        unifiedData.id = data.id;
        unifiedData.code = data.code;
        unifiedData.days = data.days;
        unifiedData.destinationName = data.destination_name;
        unifiedData.destinationCode = data.destination_code;
        unifiedData.name = data.name;
        unifiedData.ship_name = data.ship_name;
        unifiedData.ship_code = data.ship_code;
        unifiedData.transports = {
          plane: data?.plane,
          bus: data?.bus,
        };
        unifiedData.conditions = data.conditions;
        unifiedData.description = data.description;
        unifiedData.departureName = data.departure_name;
        unifiedData.departureDate = data.departure_at;
        unifiedData.arrivalDate = data.arrival_at;
        unifiedData.arrivalName = data.arrival_name;
        unifiedData.sliderName = data.slider_name;
        unifiedData.sliderDescription = data.slider_description;
        unifiedData.priceIncludedText = data.price_included_text;
        unifiedData.priceExcludedText = data.price_excluded_text;
        unifiedData.noteText = data.note_text;
        unifiedData.transportText = data.transport_text;
        unifiedData.label = data.label;
        unifiedData.freeUnits = {
          single: data.free_person,
          cabin: data.free_cabin,
          ship: data.free_ship,
        };

        unifiedData.prices = {
          single: data.first_adult_min_price,
          total: data.total_price,
        };

        unifiedData.delegateInfo = {
          name: data.delegate_name,
          email: data.delegate_email,
          phone: data.delegate_phone,
          text: data.delegate_text,
        };

        unifiedData.ship_stars = data.ship_stars;

        return this.getEntityFiles(data.id).then((entityFiles) => {
          const thumbnailImg = entityFiles.find(
            (file) => file.function === 'thumbnail-img',
          );
          const heroImg = entityFiles.find(
            (file) => file.function === 'hero-img',
          );
          const galleryImgs = entityFiles.filter(
            (file) => file.function === 'gallery-img',
          );
          const slidesImgs = entityFiles.filter(
            (file) => file.function === 'slides-img',
          );
          const slidesFile = entityFiles.find(
            (file) => file.function === 'slides-file',
          );
          const delegateImg = entityFiles.find(
            (file) => file.function === 'delegate-img',
          );

          if (thumbnailImg) {
            unifiedData.image = `${API_BASE}/file/${thumbnailImg.id}`;
          } else {
            unifiedData.image = './';
          }

          if (heroImg) {
            unifiedData.heroImage = `${API_BASE}/file/${heroImg.id}`;
          } else {
            unifiedData.heroImage = './';
          }

          if (delegateImg) {
            unifiedData.delegateImage = `${API_BASE}/file/${delegateImg.id}`;
          } else {
            unifiedData.delegateImage = './';
          }

          unifiedData.gallery = galleryImgs.map((img) => {
            img.url = `${API_BASE}/file/${img.id}`;
            return img;
          });

          unifiedData.slides = slidesImgs.map((img) => {
            img.url = `${API_BASE}/file/${img.id}`;
            return img;
          });

          if (slidesFile) {
            unifiedData.slidesFile = `${API_BASE}/file/${slidesFile.id}`;
          }

          return unifiedData;
        });
      }),
    );
  }

  /**
   * Currently only implemented for Costa Cruises.
   * @param {string} cruiseCode Code of the cruise
   * @returns Data from external API
   */
  @traceMethod
  async getExternalCruise(cruiseCode) {
    return axios(`${API_BASE}/external/info/${cruiseCode}`).then((resp) => {
      const data = resp.data;

      if (!data) {
        return false;
      }

      return data;
    });
  }

  /**
   * Fetches the list of available airports from the external API.
   * @param {string} cruiseCode
   * @param {string|null} priceCode Ignored used for Costa Cruises
   * @returns List of available airports as key-value pairs in an object
   */
  @traceMethod
  async getExternalAirports(cruiseCode, priceCode) {
    console.debug(priceCode);
    return fetch(`${API_BASE}/external/airports/${cruiseCode}`, {
      body: JSON.stringify({ price: priceCode }),
      method: 'POST',
    }).then((resp) => resp.json());
  }

  /**
   * Fetches the list of all known categories
   * @returns Array category codes
   */
  @traceMethod
  async getCategories() {
    return axios(`${API_BASE}/category-list`).then((resp) => {
      const data = resp.data;

      return data;
    });
  }
}
